import classNames from 'classnames'
import React from 'react'
import Link from './link'
import Media from './media'

export function CardHorizontal({ content, media, tags, post, ...props }) {
  const postCard = post?.acf?.meta?.card
  const kicker = content?.kicker ?? postCard?.content?.kicker
  const heading = content?.heading ?? postCard?.content?.heading
  const subheading = content?.subheading ?? postCard?.content?.subheading
  const description = content?.description ?? postCard?.content?.description
  const link = content?.link ?? postCard?.content?.link

  const children = (
    <div className="border-secondary rounded-xxx group flex transform-gpu cursor-pointer flex-col overflow-hidden border-2 shadow-sm duration-200 ease-in-out hover:scale-x-[1.01] hover:shadow-lg md:flex-row md:items-stretch">
      <div className="card-header-horizontal border-secondary overflow-hidden border-b-2 md:border-b-0 md:border-r-2">
        <Media
          autoPlay
          preload="auto"
          muted
          loop
          cover
          className="aspect-square h-full w-full object-cover duration-300 ease-in-out"
          {...media}
        />
      </div>
      <div className="text-secondary flex flex-col justify-between p-4 md:py-6 md:pl-6 md:pr-12">
        <div>
          {kicker && (
            <div
              className="accent-bs"
              dangerouslySetInnerHTML={{
                __html: kicker.replaceAll(
                  /\*(?<em>.*?)\*/g,
                  '<span class="emphasis">$<em></span>'
                )
              }}
            />
          )}
          {heading && <h3 className="heading-bs mt-1 md:mt-2">{heading}</h3>}
          {subheading && <div className="accent-bs mt-2">{subheading}</div>}
          {description && <p className="body-bs mt-4">{description}</p>}
        </div>
        {tags?.length > 0 && (
          <ul className="mt-8 flex flex-wrap gap-2">
            {tags.map((tag, index) => (
              <li
                className="rounded-xxx border-secondary body-bs flex-none border px-4 py-1 first:ml-0"
                key={index}>
                {tag}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )

  return (
    <article {...props}>
      {link ? <Link {...link}>{children}</Link> : children}
    </article>
  )
}

export function CardVertical({
  content,
  media,
  size,
  post,
  className,
  ...props
}) {
  const postCard = post?.acf?.meta?.card
  const heading = content?.heading ?? postCard?.content?.heading
  const subheading = content?.subheading ?? postCard?.content?.subheading
  const description = content?.description ?? postCard?.content?.description
  const link = content?.link ?? postCard?.content?.link
  let image = postCard?.media?.image
  let video = postCard?.media?.video

  if (media?.image || media?.video) {
    image = media?.image
    video = media?.video
  }

  const children = (
    <>
      {media && (
        <div
          className={classNames(
            'rounded-t-xxx transform-gpu overflow-hidden border-x-2 border-t-2 ease-in-out',
            size === 'lg'
              ? '[.breaker_&]:border-t-primary [.breaker_&]:border-x-primary lg:[.breaker_.cards-card:nth-child(1)_&]:border-x-primary lg:[.breaker_.cards-card:nth-child(1)_&]:border-t-primary lg:[.breaker_.cards-card:nth-child(2)_&]:border-x-primary lg:[.breaker_.cards-card:nth-child(2)_&]:border-t-primary lg:[.breaker_&]:border-t-secondary lg:[.breaker_&]:border-x-secondary'
              : 'border-secondary'
          )}>
          <Media
            autoPlay
            preload="auto"
            muted
            loop
            cover
            className="aspect-[3/2] duration-300 ease-in-out lg:aspect-[5/3]"
            image={image}
            video={video}
          />
        </div>
      )}
      <div className="rounded-b-xxx border-secondary flex grow flex-col justify-between border-2 p-6">
        <div>
          {heading && (
            <h3
              className={classNames(
                'text-secondary',
                {
                  sm: 'heading-sm',
                  lg: 'heading-bs'
                }[size]
              )}>
              {heading}
            </h3>
          )}
          {subheading && (
            <div className="accent-bs text-secondary">{subheading}</div>
          )}
          {description && (
            <p className="body-bs 420:text-secondary exxxotica:text-tertiary">
              {description}
            </p>
          )}
        </div>
      </div>
    </>
  )

  return (
    <article
      className={classNames('flex h-full flex-col', className)}
      {...props}>
      {link ? (
        <Link
          className="rounded-xxx group mt-[1px] flex grow cursor-pointer flex-col shadow-sm duration-200 ease-in-out hover:scale-x-[1.01] hover:shadow-lg"
          {...link}>
          {children}
        </Link>
      ) : (
        children
      )}
    </article>
  )
}

export default function Card({ type, ...props }) {
  if (!type) {
    throw new Error('<Card> error: The `type` prop is required.')
  }

  if (!['horizontal', 'vertical'].includes(type)) {
    throw new Error(
      '<Card> error: The `type` prop must be either `"horizontal"` or `"vertical"`.'
    )
  }

  return {
    horizontal: <CardHorizontal {...props} />,
    vertical: <CardVertical {...props} />
  }[type]
}
